import { useEffect, useState, useCallback } from "react";
import { Search, SlidersHorizontal, Heart, ListMusic } from "lucide-react";
import api from "@/lib/api";
import SongCard from "@/components/SongCard";
import SetlistBar from "@/components/SetlistBar";

export default function Library() {
  const [songs, setSongs] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [favOnly, setFavOnly] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const load = useCallback(async () => {
    const { data } = await api.get("/songs", { params: { search, sort, favorite: favOnly } });
    setSongs(data);
  }, [search, sort, favOnly]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const favorite = async (song) => {
    await api.post(`/songs/${song.id}/favorite`);
    load();
  };
  const del = async (song) => {
    if (!window.confirm(`Delete "${song.title}"?`)) return;
    await api.delete(`/songs/${song.id}`);
    load();
  };

  const toggleSelect = (song) =>
    setSelectedIds((ids) => (ids.includes(song.id) ? ids.filter((i) => i !== song.id) : [...ids, song.id]));

  const exitSelect = () => { setSelectMode(false); setSelectedIds([]); };
  const selectedSongs = selectedIds.map((id) => songs.find((s) => s.id === id)).filter(Boolean);
  const reorder = (newIds) => setSelectedIds(newIds);

  return (
    <div className={`space-y-5 sw-fade-up ${selectMode ? "pb-44 md:pb-28" : ""}`}>
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">My Library</h1>
          <p className="mt-1 text-sm text-slate-400">{songs.length} song{songs.length !== 1 ? "s" : ""}</p>
        </div>
        <button
          data-testid="setlist-mode-btn"
          onClick={() => (selectMode ? exitSelect() : setSelectMode(true))}
          className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
            selectMode ? "bg-cyan-500 text-[#0B0E14]" : "bg-[#131822] text-slate-300 ring-1 ring-[#222A38] hover:text-white"
          }`}
        >
          <ListMusic size={16} /> {selectMode ? "Done" : "Setlist"}
        </button>
      </header>

      {selectMode && (
        <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-3 text-xs text-cyan-200/80">
          Select analyzed songs to build a printable setlist of chord charts &amp; lyric sheets. (DEMO data)
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            data-testid="library-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or artist…"
            className="w-full rounded-xl border border-[#222A38] bg-[#131822] py-2.5 pl-9 pr-3 text-sm text-white placeholder-slate-500 outline-none focus:border-cyan-500/60"
          />
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <SlidersHorizontal size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <select
              data-testid="library-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="appearance-none rounded-xl border border-[#222A38] bg-[#131822] py-2.5 pl-8 pr-8 text-sm text-white outline-none focus:border-cyan-500/60"
            >
              <option value="recent">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="title">Title A–Z</option>
              <option value="artist">Artist A–Z</option>
            </select>
          </div>
          <button
            data-testid="library-fav-filter"
            onClick={() => setFavOnly((v) => !v)}
            className={`flex items-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors ${
              favOnly ? "border-rose-500/40 bg-rose-500/10 text-rose-300" : "border-[#222A38] bg-[#131822] text-slate-400"
            }`}
          >
            <Heart size={15} className={favOnly ? "fill-rose-400" : ""} /> Favs
          </button>
        </div>
      </div>

      {songs.length ? (
        <div className="grid gap-3">
          {songs.map((s) => (
            <SongCard
              key={s.id}
              song={s}
              onFavorite={favorite}
              onDelete={del}
              selectable={selectMode}
              selected={selectedIds.includes(s.id)}
              onSelect={toggleSelect}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-[#222A38] bg-[#131822]/50 p-10 text-center text-sm text-slate-500">
          No songs found.
        </div>
      )}

      {selectMode && selectedSongs.length > 0 && (
        <SetlistBar selected={selectedSongs} onClear={exitSelect} onReorder={reorder} />
      )}
    </div>
  );
}
